import type { ListingCardProps } from "@/components/cards/listing-card";
import { formatEventDateLabel, parseHotelRating } from "@/lib/utils";
import { toArticleCardFields } from "@/lib/articles/article-card";
import type {
  ListingItem,
  ListingType,
  ArticleListingItem,
  FoodDrinkListingItem,
  EventListingItem,
  HotelListingItem,
  VendorListingItem,
  ShoppingGuideListingItem,
} from "./directory-listing.types";

type FilterLayout = "pills" | "toolbar";

export interface FilterGroupConfig {
  key: string;
  label: string;
  paramName: string;
}

interface ListingTypeConfig {
  label: string;
  filterLayout: FilterLayout;
  featuredFirstRow?: boolean;
  filterGroups?: FilterGroupConfig[];
  hasDateFilter?: boolean;
  hasTimeFilter?: boolean;
  hideAlphabetBar?: boolean;
  hideStatusBar?: boolean;
  mapToCardProps: (item: ListingItem) => ListingCardProps;
}

export const LISTING_TYPE_CONFIGS: Record<ListingType, ListingTypeConfig> = {
  articles: {
    label: "articles",
    filterLayout: "pills",
    featuredFirstRow: true,
    hideStatusBar: true,
    mapToCardProps: (item) => ({
      variant: "article",
      ...toArticleCardFields(item as ArticleListingItem),
    }),
  },
  stores: {
    label: "stores",
    filterLayout: "toolbar",
    filterGroups: [
      { key: "districts", label: "Districts", paramName: "district" },
    ],
    mapToCardProps: (item) => ({
      variant: "store",
      title: item.title ?? "",
      href: item.slug ?? "#",
      visual: item.visual,
      district: "district" in item ? (item.district ?? undefined) : undefined,
      isLogo: item.isLogo ?? undefined,
    }),
  },
  events: {
    label: "events",
    filterLayout: "toolbar",
    filterGroups: [
      { key: "eventTypes", label: "Types", paramName: "type" },
      { key: "districts", label: "Districts", paramName: "district" },
    ],
    hasDateFilter: true,
    hasTimeFilter: true,
    hideAlphabetBar: true,
    mapToCardProps: (item) => {
      const e = item as EventListingItem;
      const dateLabel =
        e.startDate && e.endDate
          ? formatEventDateLabel(e.startDate, e.endDate)
          : "";
      return {
        variant: "event",
        title: e.title ?? "",
        href: e.slug ?? "#",
        visual: e.visual,
        dateLabel,
        description: e.description ?? undefined,
        district: e.district ?? undefined,
        isLogo: e.isLogo ?? undefined,
      };
    },
  },
  hotels: {
    label: "hotels",
    filterLayout: "toolbar",
    filterGroups: [
      { key: "hotelRatings", label: "Ratings", paramName: "rating" },
      { key: "amenities", label: "Amenities", paramName: "amenity" },
      { key: "districts", label: "Districts", paramName: "district" },
    ],
    mapToCardProps: (item) => {
      const h = item as HotelListingItem;
      return {
        variant: "hotel",
        title: h.title ?? "",
        // Hotels link out to their external website, not an internal page.
        href: h.hotelUrl ?? null,
        visual: h.visual,
        rating: parseHotelRating(h.rating),
        district: h.district ?? undefined,
      };
    },
  },
  foodDrink: {
    label: "restaurants",
    filterLayout: "toolbar",
    filterGroups: [
      // Districts filter hidden until ingest stops mislabelling buildings as districts
      { key: "cuisines", label: "Cuisines", paramName: "cuisine" },
      {
        key: "dietaryRequirements",
        label: "Dietary Requirements",
        paramName: "dietary",
      },
      { key: "ratings", label: "Ratings", paramName: "rating" },
    ],
    // District hidden for now — staging district data is unreliable;
    // the building serves as the card's location line instead
    mapToCardProps: (item) => {
      const f = item as FoodDrinkListingItem;
      return {
        variant: "store",
        title: f.title ?? "",
        href: f.slug ?? "#",
        visual: f.visual,
        district: f.building ?? undefined,
        categories: f.categories
          .map((c) => c.name)
          .filter((n): n is string => n != null),
        isLogo: f.isLogo ?? undefined,
      };
    },
  },
  attractions: {
    label: "attractions",
    filterLayout: "toolbar",
    filterGroups: [
      { key: "attractionTypes", label: "Types", paramName: "type" },
      { key: "districts", label: "Districts", paramName: "district" },
    ],
    mapToCardProps: (item) => ({
      variant: "store",
      title: item.title ?? "",
      href: item.slug ?? "#",
      visual: item.visual,
      district: "district" in item ? (item.district ?? undefined) : undefined,
      isLogo: item.isLogo ?? undefined,
    }),
  },
  vendors: {
    label: "vendors",
    filterLayout: "toolbar",
    filterGroups: [{ key: "markets", label: "Markets", paramName: "market" }],
    mapToCardProps: (item) => {
      const v = item as VendorListingItem;
      return {
        variant: "vendor",
        title: v.title ?? "",
        href: v.slug ?? "#",
        visual: v.visual,
        market: v.market ?? undefined,
      };
    },
  },
  shoppingGuides: {
    label: "shopping guides",
    filterLayout: "pills",
    mapToCardProps: (item) => {
      const sg = item as ShoppingGuideListingItem;
      return {
        variant: "shoppingGuide",
        title: sg.title ?? "",
        href: sg.slug ?? "#",
        visual: sg.visual,
      };
    },
  },
};
